import React, { useMemo, useState } from "react";
import { formatDateTime } from "../graphView/formatters";
import { buildPsdVsDateRows } from "../graphView/psdMetrics";
import { buildFishRowsForBinsFromDetail } from "../pondStat/buildFishRowsForBins";
import { binPercentsAndCumulative } from "../pondStat/cumulativeBinMetrics";
import { LABEL } from "../../../utils/samplingDisplayLabels";

/**
 * PSD data table — collapsed by default so Generate can paint the chart first
 * without blocking on a large DOM table (sessions × species).
 */
export function PsdTableSupplement({ pondDetails, selectedFish, selectedUnit, precomputedRows }) {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    if (!expanded) return [];
    if (Array.isArray(precomputedRows) && precomputedRows.length > 0) {
      return [...precomputedRows].sort(
        (a, b) =>
          String(a.species || "").localeCompare(String(b.species || "")) ||
          (b.dateMs || 0) - (a.dateMs || 0)
      );
    }
    return buildPsdVsDateRows(pondDetails, selectedFish, selectedUnit?.value)
      .map((r) => ({
        date: r.dateLabel,
        dateMs: r.dateMs,
        species: r.species,
        nDen: r.nDenominator,
        psdQ: r.psdQ,
        psdP: r.psdP,
        psdM: r.psdM,
        psdT: r.psdT,
      }))
      .sort(
        (a, b) =>
          String(a.species || "").localeCompare(String(b.species || "")) ||
          (b.dateMs || 0) - (a.dateMs || 0)
      );
  }, [expanded, precomputedRows, pondDetails, selectedFish, selectedUnit?.value]);

  const sessionCount = pondDetails?.length || 0;

  return (
    <div className="mt-4 sampling-pdf-exclude" data-pdf-exclude="true">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded
          ? "Hide proportional size distribution table"
          : `Show proportional size distribution table${
              sessionCount ? ` (${sessionCount} session${sessionCount === 1 ? "" : "s"})` : ""
            }`}
      </button>
      {expanded && rows.length > 0 && (
        <div className="mt-2">
          <h3 className="text-base font-semibold mb-2">
            Proportional size distribution (table)
          </h3>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Species</th>
                  <th className="text-end">{LABEL.NUM_STOCK}</th>
                  <th className="text-end">PSD-Q %</th>
                  <th className="text-end">PSD-P %</th>
                  <th className="text-end">PSD-M %</th>
                  <th className="text-end">PSD-T %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.date}-${r.species}-${i}`}>
                    <td>{r.date}</td>
                    <td>{r.species}</td>
                    <td className="text-end">{r.nDen ?? r.nDenominator}</td>
                    <td className="text-end">
                      {r.psdQ == null ? "—" : Number(r.psdQ).toFixed(1)}
                    </td>
                    <td className="text-end">
                      {r.psdP == null ? "—" : Number(r.psdP).toFixed(1)}
                    </td>
                    <td className="text-end">
                      {r.psdM == null ? "—" : Number(r.psdM).toFixed(1)}
                    </td>
                    <td className="text-end">
                      {r.psdT == null ? "—" : Number(r.psdT).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {expanded && rows.length === 0 && (
        <p className="text-muted mt-2 mb-0">No PSD table rows for the current filters.</p>
      )}
    </div>
  );
}

export function BinTableSupplement({ focusDetail, selectedFish, selectedUnit, selectedBinSize }) {
  const data = useMemo(() => {
    if (!focusDetail) return null;
    const fishRows = buildFishRowsForBinsFromDetail(focusDetail, selectedFish, selectedUnit);
    const lengths = fishRows.map((r) => r.length).filter(Number.isFinite);
    if (!lengths.length) return null;
    return binPercentsAndCumulative(
      fishRows,
      { unitValue: selectedUnit.value, binSizeValue: selectedBinSize?.value },
      Math.min(...lengths),
      Math.max(...lengths)
    );
  }, [focusDetail, selectedFish, selectedUnit, selectedBinSize]);

  if (!data) return null;

  return (
    <div className="mt-4 sampling-pdf-exclude" data-pdf-exclude="true">
      <h3 className="text-base font-semibold mb-2">Bin table — {formatDateTime(focusDetail.Date)}</h3>
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>Bin</th>
              <th className="text-end">{LABEL.NUM}</th>
              <th className="text-end">% Pop</th>
              <th className="text-end">{LABEL.CUM_PCT}</th>
            </tr>
          </thead>
          <tbody>
            {data.binLabels.map((bin, i) => (
              <tr key={bin}>
                <td>{bin}</td>
                <td className="text-end">{data.numbers[i]}</td>
                <td className="text-end">{data.percents[i].toFixed(2)}</td>
                <td className="text-end">{data.cumulative[i].toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
